import {
  type Config,
  type ConfigLayer,
  DEFAULT_CONFIG,
  type IceServer,
  validateLayer,
} from "@codeshare/config";

export interface IceRow {
  /** One or more stun:/turn: URLs, separated by spaces or commas. */
  urls: string;
  username: string;
  credential: string;
}

/** The connection settings dialog's fields. */
export interface ConnectionForm {
  /** Off: the deployment's ICE servers (Google's public STUN by default). */
  customIce: boolean;
  iceServers: IceRow[];
  /** Only relayed (TURN) connections: to check that a TURN server works. */
  relayOnly: boolean;
  /** Off: the deployment's Nostr relays. */
  customRelays: boolean;
  /** One relay URL per line. */
  relays: string;
  /** Go through an iroh relay when a direct connection to someone fails. */
  fallbackRelay: boolean;
  /** Off: the deployment's iroh relays. */
  customIrohRelays: boolean;
  /** One iroh relay URL per line. */
  irohRelays: string;
}

const splitList = (text: string) => text.split(/[\s,]+/).filter(Boolean);

export function formFromSettings(layer: ConfigLayer): ConnectionForm {
  const nostr = layer.signaling?.find((s) => s.type === "nostr");
  return {
    customIce: layer.iceServers !== undefined,
    iceServers: (layer.iceServers ?? []).map((s) => ({
      urls: (Array.isArray(s.urls) ? s.urls : [s.urls]).join(" "),
      username: s.username ?? "",
      credential: s.credential ?? "",
    })),
    relayOnly: layer.iceTransportPolicy === "relay",
    customRelays: nostr !== undefined,
    relays: nostr?.relays.join("\n") ?? "",
    fallbackRelay: layer.irohRelays === undefined || layer.irohRelays.length > 0,
    customIrohRelays: layer.irohRelays !== undefined && layer.irohRelays.length > 0,
    irohRelays: layer.irohRelays?.join("\n") ?? "",
  };
}

/** The settings layer to save, and what's wrong with the form (entries that will be dropped). */
export function settingsFromForm(
  form: ConnectionForm,
  /** What applies without custom servers (the deployment's config). */
  base: Pick<Config, "iceServers"> = DEFAULT_CONFIG,
): { layer: ConfigLayer; problems: string[] } {
  const problems: string[] = [];
  const raw: Record<string, unknown> = {};
  if (form.customIce) {
    raw.iceServers = form.iceServers
      .filter((row) => row.urls.trim())
      .map((row) => {
        const urls = splitList(row.urls);
        const server: IceServer = { urls: urls.length === 1 ? (urls[0] as string) : urls };
        if (row.username) server.username = row.username;
        if (row.credential) server.credential = row.credential;
        return server;
      });
  }
  if (form.relayOnly) raw.iceTransportPolicy = "relay";
  if (form.customRelays) {
    const relays = splitList(form.relays);
    if (relays.length > 0) raw.signaling = [{ type: "nostr", relays }];
  }
  if (!form.fallbackRelay) raw.irohRelays = [];
  else if (form.customIrohRelays) {
    const relays = splitList(form.irohRelays);
    if (relays.length > 0) raw.irohRelays = relays;
  }
  const layer = validateLayer(raw, (message) => problems.push(message));
  if (form.customRelays && splitList(form.relays).length > 0 && !layer.signaling) {
    problems.push("no usable relay: relays need a wss:// URL");
  }
  if (form.fallbackRelay && form.customIrohRelays && layer.irohRelays?.length === 0) {
    // None of the custom relays were usable: keep the deployment's rather than turning it off.
    delete layer.irohRelays;
    if (splitList(form.irohRelays).length > 0) {
      problems.push("no usable fallback relay: iroh relays need an https:// URL");
    }
  }
  const servers = layer.iceServers ?? base.iceServers;
  const hasTurn = servers.some((s) =>
    (Array.isArray(s.urls) ? s.urls : [s.urls]).some((u) => /^turns?:/.test(u)),
  );
  if (form.relayOnly && !hasTurn) {
    problems.push("relay only needs a TURN server (turn: or turns:); connections will fail");
  }
  return { layer, problems };
}
